import * as Sentry from '@sentry/react';

/** Stays `.js`: `import.meta.env` cannot be parsed by ts-jest — see CLAUDE.md. */
const IS_DEV = import.meta.env.DEV;
const IS_PROD = import.meta.env.PROD;

const THROTTLE_MS = 30_000;
const lastSent = new Map();
const suppressed = new Map();

const throttleKey = (scope, err) => `${scope}::${err?.message ?? String(err)}`;

const shouldSend = key => {
  const now = Date.now();
  const previous = lastSent.get(key);
  if (previous != null && now - previous < THROTTLE_MS) {
    suppressed.set(key, (suppressed.get(key) ?? 0) + 1);
    return false;
  }
  lastSent.set(key, now);
  return true;
};

/**
 * A caught error, reported where it can be read later.
 *
 * `scope` is the Sentry grouping key, so it stays a stable literal — anything variable goes in
 * `context`. Never throws: it runs inside `catch` blocks.
 */
export const reportError = (scope, err, context) => {
  try {
    const key = throttleKey(scope, err);
    if (!shouldSend(key)) return;

    const repeats = suppressed.get(key) ?? 0;
    suppressed.delete(key);

    if (IS_DEV) {
      console.error(`[${scope}]`, err, context ?? '');
      return;
    }
    if (!IS_PROD) return;

    Sentry.captureException(err instanceof Error ? err : new Error(String(err)), {
      tags: { scope },
      extra: { ...context, ...(repeats ? { suppressedRepeats: repeats } : {}) }
    });
  } catch {
    // Best-effort: the caller already handled the real error.
  }
};

/** Dev-only diagnostics; a no-op in every build that ships. */
export const debug = (scope, ...args) => {
  if (!IS_DEV) return;
  console.log(`[${scope}]`, ...args);
};
